import axios from 'axios';
import { route, ShortCodeService } from '@ht/lib-plural-vue';

import jsonpath from 'jsonpath';
import types from '@/actions/types';
import useSequence from '@/composables/useSequence';
import useConfig from '@/composables/useConfig';
import useDynamicUrl from '@/composables/useDynamicUrl';
import useAnalytics from '@/composables/useAnalytics';
import { API_CALL_TIMEOUT, VARIABLE_VALUE_TYPES, API_CALL_BODY_TYPES, SYSTEM_VARIABLES_MAP } from '@/js/constants';
import Base from '@/actions/base';

const { getRobotToken, getDomain, isLocalNetworkDomain, isPluralApi } = route;

export default class ApiCall extends Base {
  constructor(params) {
    super(params, types.API_CALL);
    this.name = types.API_CALL;
    this.stopped = false;
    this.paused = false;
    this.shouldComplete = null;
  }

  execute() {
    const { runSequence } = useSequence();
    const {
      setVariables,
      getVariable,
      currentConfig,
      sharedConfig,
      isPreview,
      setFlowStateBackToStart,
    } = useConfig();
    const { trackAction, amendAction } = useAnalytics();

    const {
      payload,
      endpoint,
      method,
      headers,
      variables,
      contentType,
      extendPayload,
    } = this.params.config;
    const timeout = this.params.config.timeout ? this.params.config.timeout * 1000 : API_CALL_TIMEOUT;

    return new Promise((resolve) => {
      let data = payload || null;

      if (contentType === API_CALL_BODY_TYPES.FORM_DATA) {
        data = new FormData();
        JSON.parse(payload).forEach(({ key, value }) => {
          const keyFinal = new ShortCodeService(key)
            .matchAttributes()
            .getValue();
          const valueFinal = new ShortCodeService(value)
            .matchAttributes()
            .getValue();
          data.append(keyFinal, valueFinal);
        });
      } else if (contentType === API_CALL_BODY_TYPES.URL_ENCODED) {
        data = JSON.parse(payload).reduce((res, { key, value }) => {
          const keyFinal = new ShortCodeService(key)
            .matchAttributes()
            .getValue();
          const valueFinal = new ShortCodeService(value)
            .matchAttributes()
            .getValue();
          res.push(`${encodeURIComponent(keyFinal)}=${encodeURIComponent(valueFinal)}`);
          return res;
        }, []).join('&');
      } else if (contentType === API_CALL_BODY_TYPES.JSON && !data) {
        data = '{}';
      } else if (data) {
        data = new ShortCodeService(data)
          .matchAttributes()
          .getValue();

        // NOTE: since we can insert arbitrary text through variables, we need to make sure that
        // there is nothing breaking the json payload
        data = data
          .replace(/\\/g, '\\\\')
          .replace(/\//g, '\\/')
          .replace(/\t/g, '\\t')
          .replace(/\r/g, '\\r')
          .replace(/\f/g, '\\f')
          .replace(/\n/g, '\\n');

        if (extendPayload) {
          data = JSON.stringify({
            ...JSON.parse(data),
            organisationId: currentConfig.value.organisationId,
            organisationName: currentConfig.value.organisationName,
            robotId: currentConfig.value.robotId,
            robotName: currentConfig.value.robotName,
            robotType: 'PLURAL_ONLINE',
            speechServicesActive: currentConfig.value.speechServicesActive,
            recognitionServicesActive: currentConfig.value.recognitionServicesActive,
            forcePreview: currentConfig.value.forcePreview,
            projectId: currentConfig.value.projectId,
            projectName: currentConfig.value.projectName,
            // environment
            environment: window.location.hostname,
            deviceId: sharedConfig.value.deviceId,
            // element specific properties
            sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
            sessionStartTime: getVariable(SYSTEM_VARIABLES_MAP.SESSION_START_TIME.id).value,
            elementType: this.name,
            elementTitle: this.params?.title,
            elementId: `${this.params?.sequenceId}-${this.params?.sequenceIndex}`,
            timestamp: new Date(),
          });
        }
      }

      let url = `${useDynamicUrl(endpoint)}`;
      const useProxy = !isLocalNetworkDomain(url) && !isPluralApi(url);
      if (useProxy) {
        url = `${getDomain()}/api/cors/${url}`;
      }

      this.analyticData.customData = { request: { method, url, data, headers } };
      trackAction(this);

      if (isPreview() && this.params?.originalActionType === 'sms') {
        // we're intercepting the call because sms should not be requested within preview
        this.resolve = resolve;
        resolve();
        if (this.params.failureFlow) {
          setFlowStateBackToStart(this.params.failureFlow);
          runSequence(this.params.failureFlow);
        }
        return;
      }

      axios.request({
        method,
        url,
        data,
        timeout,
        headers: {
          'Content-Type': contentType,
          ...(useProxy ? {
            'x-ht-auth': `robot ${getRobotToken()}`,
            'x-requested-with': 'plural-online',
          } : {}),
          ...headers.reduce((acc, { key, value }) => ({
            ...acc,
            [key]: new ShortCodeService(value).matchAttributes().getValue(),
          }), {}),
        },
      })
        .then((response) => {
          if (!this.stopped) {
            this.analyticData.customData.response = response.data;
            amendAction(this.analyticData);

            // SMS send element is simple API call element but doesn't have variables
            if (variables) {
              const variablesToSet = variables.reduce((acc, { id, path }) => {
                const value = jsonpath.value(response.data, path);
                if (value !== undefined) {
                  acc.push({ id, value: { value, type: VARIABLE_VALUE_TYPES.PRIMITIVE } });
                }

                return acc;
              }, []);
              setVariables(variablesToSet);
            }

            if (!this.paused) {
              resolve(response);
              if (this.params.successFlow) {
                setFlowStateBackToStart(this.params.successFlow);
                runSequence(this.params.successFlow);
              }
            } else {
              this.shouldComplete = {
                result: response,
                next: this.params.successFlow,
              };
            }
          }
        })
        .catch((err) => {
          if (!this.paused) {
            resolve(err);
            if (!this.stopped && this.params.failureFlow) {
              setFlowStateBackToStart(this.params.failureFlow);
              runSequence(this.params.failureFlow);
            }
          } else {
            this.shouldComplete = {
              result: err,
              next: !this.stopped && this.params.failureFlow ? this.params.failureFlow : null,
            };
          }
        });
      this.resolve = resolve;
    });
  }

  stop() {
    this.stopped = true;
    this.resolve();
  }

  pause() {
    this.paused = true;
  }

  resume() {
    this.paused = false;
    if (this.shouldComplete) {
      this.resolve(this.shouldComplete.result);
      if (this.shouldComplete.next) {
        const { runSequence } = useSequence();
        const { setFlowStateBackToStart } = useConfig();

        setFlowStateBackToStart(this.shouldComplete.next);
        runSequence(this.shouldComplete.next);
      }
    }
  }
}
